/** Domain types aligned with `lib/models/app_models.dart`. */

export type CompanyRole = 'owner' | 'supervisor' | 'member' | 'viewer';

export interface Company {
  id: string;
  name: string;
  businessName?: string;
  colorHex: number;
  joinCode?: string;
  logoUrl?: string;
  description?: string;
  backgroundAsset?: string;
  autoSelectSupervisorsOnNewTasks: boolean;
  /** IST minutes-from-midnight when active duty auto-ends (default 8:00 PM). */
  dutyAutoEndMinutesIst: number;
  /**
   * Minutes a member must stay in one place before a pause marker on the duty map.
   * `0` disables stationary markers.
   */
  stationaryMarkMinutes?: number;
  /** Owner master switch: Field Duty expenses for the whole company. */
  expensesEnabled?: boolean;
  expenseHomePinCode?: string;
  expenseHomeLat?: number;
  expenseHomeLng?: number;
  expenseHomeRadiusKm: number;
  expenseTravelRatePerKm: number;
  expensePostDutyWindowHours: number;
  /**
   * Web-only quick filter capsule ids (max 10): toggle field names or `tag:…`.
   */
  quickFilterIds?: string[];
  billingPlan?: string;
  /** UTC instant complimentary Premium ends (see `company_billing_plan.dart`). */
  premiumComplimentaryEndsAt?: Date;
  /** UTC instant paid Premium is valid through. */
  premiumSubscriptionPaidThrough?: Date;
  createdAt: Date;
  createdBy: string;
}

export interface Member {
  uid: string;
  role: CompanyRole;
  displayName?: string;
  phoneNumber?: string;
  joinedAt: Date;
  backgroundAsset?: string;
  /** Completed free-trial duty sessions for this member in this workspace. */
  fieldDutyTrialSessionsUsed?: number;
}

export type TaskStatus = 'open' | 'closed';

export interface Task {
  id: string;
  companyId: string;
  title: string;
  description?: string;
  tags: string[];
  createdBy: string;
  createdAt: Date;
  lastMessageAt?: Date;
  eta?: Date;
  repeat?: string;
  repeatTime?: string;
  repeatEtaTime?: string;
  taskNumber?: number;
  taskDisplayCode?: string;
  status: TaskStatus;
  closedBy?: string;
  closedAt?: Date;
  assignees: string[];
  primaryAssigneeId?: string;
  supervisors: string[];
  hasClosureRequest: boolean;
  /** When a closure request is pending — from `closureRequest.requestedAt`. */
  closureRequestedAt?: Date;
  /** From `lastClosureRequest.requestedAt` (pending / rejected / accepted). */
  lastClosureRequestedAt?: Date;
  lastClosureRequestStatus?: string;
  closedWithoutClosureRequest: boolean;
  closedAsNotDone: boolean;
  isBlocked: boolean;
  isEtaIncreaseRequested: boolean;
  /** Closed-task appreciations: uid → emoji (👍 👏 🎉 🚀). */
  appreciations: Record<string, string>;
}

export interface TaskMessage {
  id: string;
  senderId: string;
  text: string;
  createdAt: Date;
  type: string;
  attachmentName?: string;
  /** When `markdown`, [text] is rendered with lists and emphasis. */
  textFormat?: 'plain' | 'markdown';
  fieldCheckIn?: FieldCheckInMessageData;
  editedAt?: Date;
  deletedAt?: Date;
}

export interface FieldCheckInMessageData {
  checkInId: string;
  sessionId: string;
  photoUrls: string[];
  lat: number;
  lng: number;
  userName?: string;
}

export interface DutyTaskLinkEntry {
  taskId: string;
  linkedAt: Date;
  unlinkedAt?: Date;
  taskTitle?: string;
  taskDisplayCode?: string;
}

export function roleFromFirestore(v: string | undefined): CompanyRole {
  switch ((v ?? '').toLowerCase()) {
    case 'owner':
      return 'owner';
    case 'supervisor':
      return 'supervisor';
    case 'viewer':
      return 'member';
    default:
      return 'member';
  }
}

// ---------------------------------------------------------------------------
// Field Tracker & Attendance (mirrors lib/models/field_tracker_models.dart)
// ---------------------------------------------------------------------------

export type DutyStatus = 'active' | 'ended';

export type DutyRouteMatchStatus = 'pending' | 'matching' | 'ok' | 'failed';

/** One Start→End duty period for a member; doubles as the attendance row. */
export interface DutySession {
  id: string;
  userId: string;
  userName: string;
  status: DutyStatus;
  startAt: Date;
  endAt?: Date;
  /** IST `YYYY-MM-DD` of `startAt`, used to group/filter attendance per day. */
  dateKey: string;
  durationMinutes: number;
  distanceMeters: number;
  startLat?: number;
  startLng?: number;
  endLat?: number;
  endLng?: number;
  dutyChainId?: string;
  linkedTaskId?: string;
  linkedAt?: Date;
  linkedTaskTitle?: string;
  linkedTaskDisplayCode?: string;
  linkHistory?: DutyTaskLinkEntry[];
  /** Server OSRM map-match status. */
  matchStatus?: DutyRouteMatchStatus | string;
  /** GPS path length mirrored for legacy; prefer distanceMeters. */
  matchedDistanceMeters?: number;
  /** Canonical encoded GPS trail. */
  matchedPolyline?: string;
  /** `osrm` when a confident road snap exists; otherwise `raw_gps`. */
  matchSource?: string;
  /** Legacy optional road-snapped geometry (no longer written or displayed). */
  osrmPolyline?: string;
  osrmDistanceMeters?: number;
  matchedPointCount?: number;
  matchAttempts?: number;
  matchError?: string;
  /** e.g. mock_location, impossible_jump, gps_off, tracking_gap */
  securityFlags?: string[];
  securityFlaggedAt?: Date;
  securityFlagMessage?: string;
  /** Soft GPS-off banner while Location is still off. */
  gpsOffActive?: boolean;
  /** Soft tracking-gap banner while the gap is still open. */
  trackingGapActive?: boolean;
  /** Battery % sampled while on duty (0–100). */
  batteryStartPercent?: number;
  batteryEndPercent?: number;
  batteryLastPercent?: number;
  batteryLastAt?: Date;
  /** True once GPS left the company home radius during this session. */
  expenseEligible?: boolean;
}

/** Company-level blocked location attempt (start duty / check-in / during duty). */
export interface LocationIntegrityAttempt {
  id: string;
  userId: string;
  userName: string;
  at: Date;
  dateKey: string;
  action: string;
  reason: string;
  sessionId?: string;
  lat?: number;
  lng?: number;
  accuracy?: number;
  isMocked?: boolean;
  message?: string;
}

export function dutyHasSecurityFlag(s: DutySession): boolean {
  return (s.securityFlags?.length ?? 0) > 0;
}

export function dutyActiveIntegrityFlags(s: DutySession): string[] {
  return (s.securityFlags ?? []).filter((f) => {
    if (f === 'gps_off' && s.gpsOffActive === false) return false;
    // Soft stationary quiet is never stored as a session flag; tracking_gap means
    // app closed/killed or phone powered off (detected on resume).
    if (f === 'tracking_gap' && s.trackingGapActive === false) return false;
    return true;
  });
}

/** Hard spoof always; GPS-off / tracking-interrupted while still active. */
export function dutyShowsIntegrityAlert(s: DutySession): boolean {
  const flags = s.securityFlags ?? [];
  if (dutyIntegrityIsHardFlag(flags)) return true;
  if (flags.includes('gps_off') && s.gpsOffActive !== false) return true;
  if (flags.includes('tracking_gap') && s.trackingGapActive !== false) return true;
  return false;
}

export function dutySessionExportNotes(s: DutySession): string {
  const parts: string[] = [];
  const flags = dutyActiveIntegrityFlags(s);
  if (flags.length > 0) {
    parts.push(dutyIntegrityReportLabel(flags));
    const msg = s.securityFlagMessage?.trim();
    const softGapOnly =
      !!msg && msg.includes('No location updates were received');
    if (msg && !softGapOnly) {
      parts.push(msg);
    } else {
      parts.push(dutyIntegrityFallbackMessage(flags));
    }
  }
  const start = s.batteryStartPercent;
  const end = s.batteryEndPercent ?? s.batteryLastPercent;
  if (start != null || end != null) {
    if (start != null && end != null && start !== end) {
      parts.push(`Battery ${start}%->${end}%`);
    } else {
      parts.push(`Battery ${end ?? start}%`);
    }
  }
  return parts.join(' | ');
}

export function dutyIntegrityIsHardFlag(flags: string[] | undefined): boolean {
  const set = new Set((flags ?? []).map((f) => f.trim()).filter(Boolean));
  return set.has('mock_location') || set.has('impossible_jump');
}

export function dutyIntegrityAccentColor(flags: string[] | undefined): string {
  return dutyIntegrityIsHardFlag(flags) ? '#cc0000' : '#e67e22';
}

export function dutyIntegrityReportLabel(flags: string[] | undefined): string {
  const set = new Set((flags ?? []).map((f) => f.trim()).filter(Boolean));
  if (set.has('mock_location') || set.has('impossible_jump')) {
    return 'Fake location suspected';
  }
  if (set.has('gps_off')) return 'GPS turned off';
  if (set.has('tracking_gap')) return 'Tracking stopped';
  return 'Integrity alert';
}

export function dutyIntegrityFallbackMessage(flags: string[] | undefined): string {
  const set = new Set((flags ?? []).map((f) => f.trim()).filter(Boolean));
  if (set.has('gps_off')) {
    return 'Location/GPS was turned off during duty. Tracking may have gaps until GPS is on again.';
  }
  if (set.has('tracking_gap')) {
    return 'Duty tracking stopped because the app was closed or killed during duty.';
  }
  return 'Possible fake or unreliable location detected.';
}

export function locationIntegrityAttemptLabel(action: string): string {
  switch (String(action || '').trim()) {
    case 'start_duty':
      return 'Blocked start duty';
    case 'check_in':
      return 'Blocked check-in';
    case 'during_duty':
      return 'Blocked during duty';
    default:
      return 'Location integrity attempt';
  }
}

/** Distance for UI/reports: always GPS path length (source of truth). */
export function dutyDisplayDistanceMeters(s: DutySession): number {
  return s.distanceMeters;
}

export function dutyHasMatchedRoute(s: DutySession): boolean {
  return s.matchStatus === 'ok' && !!s.matchedPolyline?.trim();
}

/** A single GPS breadcrumb captured during a duty session. */
export interface TrackPoint {
  lat: number;
  lng: number;
  at: Date;
  accuracy?: number;
  speed?: number;
  seq: number;
}

/** Last known position for one member (live team map). */
export interface LiveLocation {
  userId: string;
  userName: string;
  lat: number;
  lng: number;
  at: Date;
  onDuty: boolean;
  sessionId?: string;
  speed?: number;
  accuracy?: number;
}

/** Field check-in during a duty session (up to three photos). */
export interface DutyCheckIn {
  id: string;
  userId: string;
  userName: string;
  sessionId: string;
  description: string;
  photoUrls: string[];
  lat: number;
  lng: number;
  accuracy?: number;
  createdAt: Date;
  dateKey: string;
  taskId?: string;
}

export type ExpenseType = 'travel' | 'manual';
export type ExpenseStatus = 'pending' | 'approved' | 'rejected';

export interface DutyExpense {
  id: string;
  userId: string;
  userName: string;
  sessionId: string;
  dateKey: string;
  type: ExpenseType;
  amountInr: number;
  status: ExpenseStatus;
  description: string;
  receiptUrls: string[];
  distanceKm?: number;
  ratePerKm?: number;
  rejectionReason?: string;
  resubmitNote?: string;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface SessionExpenseSummary {
  travel: DutyExpense | null;
  manual: DutyExpense[];
  travelAmount: number;
  manualApproved: number;
  manualPending: number;
  manualRejected: number;
  manualTotal: number;
  approvedTotal: number;
  grandTotal: number;
}

export function sessionExpenseSummaryFrom(expenses: DutyExpense[]): SessionExpenseSummary {
  let travel: DutyExpense | null = null;
  let travelAmount = 0;
  let manualApproved = 0;
  let manualPending = 0;
  let manualRejected = 0;
  const manual: DutyExpense[] = [];

  for (const e of expenses) {
    if (e.type === 'travel') {
      travel = e;
      travelAmount = e.amountInr;
    } else {
      manual.push(e);
      if (e.status === 'approved') manualApproved += e.amountInr;
      else if (e.status === 'pending') manualPending += e.amountInr;
      else manualRejected += e.amountInr;
    }
  }

  const manualTotal = manualApproved + manualPending + manualRejected;
  const approvedTotal =
    (travel?.status === 'approved' ? travelAmount : 0) + manualApproved;

  return {
    travel,
    manual,
    travelAmount,
    manualApproved,
    manualPending,
    manualRejected,
    manualTotal,
    approvedTotal,
    grandTotal: travelAmount + manualTotal,
  };
}

export interface FieldMapFocus {
  lat: number;
  lng: number;
  checkInId?: string;
}

export const maxPhotosPerCheckIn = 3;
export const maxExpenseReceipts = 3;

export const personalWorkspacePrefix = 'personal_';

export function isPersonalWorkspaceId(id: string | null | undefined): boolean {
  return !!id && id.startsWith(personalWorkspacePrefix);
}

export function personalWorkspaceIdForUser(uid: string): string {
  return `${personalWorkspacePrefix}${uid}`;
}
